// Only `kind: "http"` carries a status and a body: `{ Err, Extra }`, or a string when not JSON.

export type ApiErrorKind = "http" | "network" | "timeout" | "aborted";

export type ApiErrorBody = { Err?: string; Extra?: string; Detail?: unknown; [key: string]: unknown };

export interface ApiErrorInit {
  kind: ApiErrorKind;
  status?: number;
  body?: ApiErrorBody | string | null;
  headers?: Headers | null;
  method?: string;
  url?: string;
  message?: string;
  cause?: unknown;
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  // 0 unless the server answered.
  readonly status: number;
  readonly body: ApiErrorBody | string | null;
  readonly headers: Headers | null;
  readonly method: string;
  readonly url: string;
  // Set once the response policy has acted, so a caller doesn't show it too.
  handled = false;

  constructor(init: ApiErrorInit) {
    super(init.message ?? defaultMessage(init), { cause: init.cause });
    this.name = init.kind === "aborted" ? "AbortError" : "ApiError";
    this.kind = init.kind;
    this.status = init.status ?? 0;
    this.body = init.body ?? null;
    this.headers = init.headers ?? null;
    this.method = init.method ?? "GET";
    this.url = init.url ?? "";
  }

  // The server's i18n key, when it sent one.
  get code(): string | undefined {
    const body = this.body;
    return body && typeof body === "object" && typeof body.Err === "string" ? body.Err : undefined;
  }

  get retryAfter(): number | null {
    const raw = this.headers?.get("retry-after");
    if (!raw) return null;
    const seconds = Number(raw);
    if (Number.isFinite(seconds)) return Math.max(0, seconds);
    const at = Date.parse(raw);
    return Number.isNaN(at) ? null : Math.max(0, Math.ceil((at - Date.now()) / 1000));
  }
}

function defaultMessage(init: ApiErrorInit): string {
  const target = `${init.method ?? "GET"} ${init.url ?? ""}`.trim();
  switch (init.kind) {
    case "http":
      return `${target} failed with status ${init.status ?? 0}`;
    case "network":
      return `${target} could not reach the server`;
    case "timeout":
      return `${target} timed out`;
    case "aborted":
      return `${target} was cancelled`;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isAborted(error: unknown): boolean {
  if (isApiError(error)) return error.kind === "aborted";
  return (error as { name?: unknown } | null)?.name === "AbortError";
}

export function isHandled(error: unknown): boolean {
  return (error as { handled?: unknown } | null)?.handled === true;
}

// A 429 that names no wait, or a wait already over, still shows one.
const FALLBACK_RATE_LIMIT_WAIT_SECONDS = 30;

/** Seconds to wait before trying again, or null when the error is not a rate limit. */
export function rateLimitWaitSeconds(error: unknown): number | null {
  if (!isApiError(error) || error.status !== 429) return null;
  const after = error.retryAfter ?? 0;
  return after > 0 ? after : FALLBACK_RATE_LIMIT_WAIT_SECONDS;
}
